/**
 * The advisor signatory role — the one signer the originator never creates.
 *
 * Applicants are authored in the e-signature drawer: the originator adds a card,
 * names it, maps its contact fields. The advisor has none of that (their info
 * comes from the signed-in session, and they are always rendered as "Advisor"),
 * so there is no card to add and nothing to fill in. The role exists purely to
 * hang signatures off, which is why `OneSpanConfig.roles` only carries it while
 * at least one placed box references it — a phantom advisor on a config nobody
 * asked the advisor to sign would show up as a signer in the runtime roster.
 *
 * That "only while used" rule is what this module owns, on behalf of both
 * writers (the drawer and the placement page). The placement page offers the
 * advisor unconditionally — see `advisorRole` — and `withAdvisorRole` puts the
 * role into `roles` the moment a box lands on them, taking it back out when the
 * last one is deleted.
 */

import type { OneSpanConfig, SignatoryRole, SignatureDocument } from '@/api/workflows';

/**
 * The advisor's wire slug. Hard-coded rather than derived: there is exactly one
 * advisor per config, and nothing about them is originator-typed, so there is
 * nothing to derive it from.
 */
export const ADVISOR_SLUG = 'advisor';

/**
 * The advisor role in this config, or a fresh one ready to be placed against.
 *
 * The placement page lists signers from `roles`, and boxes can only be dragged
 * for a signer in that list — so a config whose advisor role has not been
 * materialized yet would offer no way to ever materialize it. Callers use this
 * to show the advisor regardless; `withAdvisorRole` persists them on first
 * placement.
 *
 * The existing role is preferred whenever there is one, so an advisor persisted
 * under some other slug keeps it.
 */
export function advisorRole(roles: readonly SignatoryRole[]): SignatoryRole {
  const existing = roles.find((r) => r.type === 'advisor');
  if (existing) return existing;

  // The advisor signs last, so they take the index after every applicant.
  // `index` must be unique and >= 1 (the domain enforces both); max+1 satisfies
  // that against any applicant numbering, including one with gaps.
  const nextIndex = roles.reduce((max, r) => Math.max(max, r.index), 0) + 1;
  return {
    slug: ADVISOR_SLUG,
    type: 'advisor',
    index: nextIndex,
    // Both null by domain rule: advisor contact info comes from the BFF session,
    // and the display name is always "Advisor".
    fieldMappings: null,
    labelEn: null,
    labelFr: null,
  };
}

/**
 * `roles` with the advisor added when a signature box references them, and
 * removed when none does.
 *
 * Called on every write from the placement page so the role tracks the boxes
 * without the page having to think about it. Idempotent, and returns `roles`
 * untouched when nothing needs to change — applicant roles are never reordered
 * or renumbered here.
 */
export function withAdvisorRole(
  roles: readonly SignatoryRole[],
  documents: readonly SignatureDocument[],
): SignatoryRole[] {
  const existing = roles.find((r) => r.type === 'advisor');
  const slug = existing?.slug ?? ADVISOR_SLUG;
  const used = documents.some((doc) => doc.fields.some((f) => f.role === slug));

  if (used && !existing) return [...roles, advisorRole(roles)];
  if (!used && existing) return roles.filter((r) => r !== existing);
  return [...roles];
}

/** `config` with its advisor role reconciled against its current placements. */
export function reconcileAdvisorRole(config: OneSpanConfig): OneSpanConfig {
  return { ...config, roles: withAdvisorRole(config.roles, config.documents) };
}
